/**
 * WIPImpactsTable Component
 *
 * Floating table inset for the WIP district view.
 * Displays district-level impact forecasts with dynamic columns driven by
 * the storm impact model's variable definitions (same pattern as HIP).
 */

import { useState, useMemo, useEffect } from "react";
import { ArrowUpDown, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import AbstractInset, { BaseInsetProps, InsetState } from "./AbstractInset";
import {
  VariableSelectionDropdown,
  type VariableOption,
} from "./VariableSelectionDropdown";
import type { WIPImpactForecast } from "@/api/types/wip";

interface WIPImpactsTableProps extends Omit<BaseInsetProps, "data" | "headers"> {
  impacts: WIPImpactForecast[];
  onRowClick?: (impact: WIPImpactForecast) => void;
  modelVariables?: {
    slug: string;
    name: string;
    units?: string | null;
    order?: number;
  }[];
  selectorVariables?: {
    id: string;
    label: string;
    units?: string | null;
  }[];
}

type TableRowData = Record<string, string | number | undefined> & {
  rowId: string;
  name: string;
};

type CsvColumn = {
  key: string;
  label: string;
};

const formatFallbackLabel = (key: string) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatCsvCellValue = (val: unknown): string => {
  if (val == null) return "-";
  if (typeof val === "number")
    return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
  return String(val);
};

const escapeCSVValue = (value: string): string => {
  if (value.includes(",") || value.includes('"') || value.includes("\n")) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
};

export function createWIPImpactsCsv({
  rows,
  columns,
}: {
  rows: Array<Record<string, unknown>>;
  columns: CsvColumn[];
}) {
  const headers = columns.map((column) => escapeCSVValue(column.label));
  const csvRows = rows.map((row) =>
    columns.map((column) =>
      escapeCSVValue(formatCsvCellValue(row[column.key])),
    ),
  );

  return [headers.join(","), ...csvRows.map((row) => row.join(","))].join("\n");
}

const WIPImpactsTable: React.FC<WIPImpactsTableProps> = ({
  impacts,
  loading = false,
  error = null,
  position = "bottom-left",
  width = "500px",
  height = "360px",
  title = "Impact Predictions",
  subtitle,
  onRowClick,
  isCollapsible = true,
  initiallyCollapsed = false,
  className,
  panelMode = false,
  modelVariables = [],
  selectorVariables = [],
}) => {
  const [sortColumn, setSortColumn] = useState<string>("name");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const [pageIndex, setPageIndex] = useState(0);
  const [visibleColumnKeys, setVisibleColumnKeys] = useState<string[]>(["name"]);
  const pageSize = 8;

  const dedupedImpacts = useMemo(() => {
    const map = new Map<string, WIPImpactForecast>();
    impacts.forEach((i) => map.set(i.geographic_node_id, i));
    return map;
  }, [impacts]);

  const tableData = useMemo<TableRowData[]>(() => {
    return Array.from(dedupedImpacts.values()).map((impact) => ({
      rowId: impact.geographic_node_id,
      name: impact.geographic_node_name,
      ...impact.variable_values,
    }));
  }, [dedupedImpacts]);

  const allColumns = useMemo(() => {
    const cols: Array<{
      key: string;
      label: string;
      width: string;
      alwaysVisible?: boolean;
    }> = [
      { key: "name", label: "Prediction Area", width: "140px", alwaysVisible: true },
    ];

    const availableDataKeys = Array.from(
      new Set(
        tableData.flatMap((row) =>
          Object.keys(row).filter(
            (k) =>
              k !== "rowId" &&
              k !== "name" &&
              row[k as keyof typeof row] !== undefined &&
              row[k as keyof typeof row] !== null,
          ),
        ),
      ),
    );

    const metadataBySlug = new Map(
      modelVariables.map((v) => [v.slug, v] as const),
    );

    if (selectorVariables.length > 0) {
      selectorVariables
        .map((v) => v.id)
        .filter((k) => availableDataKeys.includes(k))
        .forEach((key) => {
          const sv = selectorVariables.find((v) => v.id === key);
          const meta = metadataBySlug.get(key);
          const units = meta?.units ?? sv?.units ?? null;
          const baseLabel = sv?.label ?? meta?.name ?? formatFallbackLabel(key);
          const label = `${baseLabel}${units ? ` (${units})` : ""}`;
          const width = `${Math.min(Math.max(label.length * 8, 110), 220)}px`;
          cols.push({ key, label, width });
        });
      return cols;
    }

    const withMeta = availableDataKeys
      .filter((k) => metadataBySlug.has(k))
      .map((k) => ({ key: k, meta: metadataBySlug.get(k)! }))
      .sort((a, b) => {
        const ao = a.meta.order ?? Number.MAX_SAFE_INTEGER;
        const bo = b.meta.order ?? Number.MAX_SAFE_INTEGER;
        return ao !== bo ? ao - bo : a.meta.name.localeCompare(b.meta.name);
      });

    const withoutMeta = availableDataKeys
      .filter((k) => !metadataBySlug.has(k))
      .sort();

    withMeta.forEach(({ key, meta }) => {
      const label = `${meta.name}${meta.units ? ` (${meta.units})` : ""}`;
      const width = `${Math.min(Math.max(label.length * 8, 110), 220)}px`;
      cols.push({ key, label, width });
    });

    withoutMeta.forEach((key) => {
      const label = formatFallbackLabel(key);
      const width = `${Math.min(Math.max(label.length * 8, 110), 220)}px`;
      cols.push({ key, label, width });
    });

    return cols;
  }, [tableData, modelVariables, selectorVariables]);

  const columns = useMemo(
    () => allColumns.filter((c) => c.alwaysVisible || visibleColumnKeys.includes(c.key)),
    [allColumns, visibleColumnKeys],
  );

  const availableVariables = useMemo<VariableOption[]>(
    () =>
      allColumns
        .filter((c) => !c.alwaysVisible)
        .map((c, idx) => {
          const meta = modelVariables.find((v) => v.slug === c.key);
          return {
            id: c.key,
            slug: c.key,
            name: c.label,
            units: meta?.units ?? null,
            order: meta?.order ?? idx,
          };
        }),
    [allColumns, modelVariables],
  );

  const selectedVariables = useMemo(
    () => visibleColumnKeys.filter((k) => k !== "name"),
    [visibleColumnKeys],
  );

  useEffect(() => {
    const dataKeys = allColumns.filter((c) => !c.alwaysVisible).map((c) => c.key);

    setVisibleColumnKeys((prev) => {
      const prevData = prev.filter((k) => k !== "name");
      const valid = prevData.filter((k) => dataKeys.includes(k));
      const shouldDefault =
        valid.length === 0 && dataKeys.length > 0;

      const nextData = shouldDefault ? dataKeys.slice(0, 3) : valid;
      return ["name", ...nextData];
    });
  }, [allColumns]);

  useEffect(() => {
    const dataKeys = allColumns.filter((c) => !c.alwaysVisible).map((c) => c.key);
    if (sortColumn === "name" || dataKeys.includes(sortColumn)) return;
    setSortColumn(dataKeys[0] ?? "name");
    setSortDirection("desc");
  }, [allColumns, sortColumn]);

  const processedData = useMemo(() => {
    const sorted = [...tableData].sort((a, b) => {
      const av = a[sortColumn as keyof typeof a];
      const bv = b[sortColumn as keyof typeof b];
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number")
        return sortDirection === "asc" ? av - bv : bv - av;
      return sortDirection === "asc"
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    });
    return sorted;
  }, [tableData, sortColumn, sortDirection]);

  const pageCount = Math.ceil(processedData.length / pageSize);
  const paginatedData = useMemo(() => {
    const start = pageIndex * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, pageIndex, pageSize]);

  useEffect(() => setPageIndex(0), [impacts]);

  const handleSort = (key: string) => {
    if (sortColumn === key) {
      setSortDirection((p) => (p === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(key);
      setSortDirection("desc");
    }
  };

  const fmt = formatCsvCellValue;

  const handleExportCSV = () => {
    if (!processedData.length) return;
    const csv = createWIPImpactsCsv({
      rows: processedData,
      columns,
    });
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.href = url;
    link.download = `wip_impacts_${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  };

  return (
    <AbstractInset
      data={impacts}
      loading={loading}
      error={error}
      position={position}
      width={width}
      height={height}
      title={title}
      subtitle={subtitle}
      isCollapsible={isCollapsible}
      initiallyCollapsed={initiallyCollapsed}
      className={className}
      panelMode={panelMode}
      containerId="wip-impacts-table-inset"
      infoTooltip={
        <div className="text-sm space-y-2">
          <p className="font-medium">Impact Predictions</p>
          <ul className="text-xs space-y-1 list-disc pl-4">
            <li>District-level winter storm impacts</li>
            <li>Click column headers to sort</li>
          </ul>
        </div>
      }
    >
      {(_insetState: InsetState) => (
        <div className="relative h-full min-h-[280px] flex flex-col">
          {error && (
            <div className="p-3 bg-destructive/10 border-b border-destructive/20">
              <p className="text-xs text-destructive">{error}</p>
            </div>
          )}

          {loading && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center p-6 text-center bg-background/80 backdrop-blur-[1px]">
              <div className="inline-block h-4 w-4 border-2 border-t-primary rounded-full animate-spin" />
              <p className="mt-2 text-xs text-muted-foreground">
                Loading impacts...
              </p>
            </div>
          )}

          {!loading && tableData.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-xs text-muted-foreground">
                No impact data available. Run the model to see predictions.
              </p>
            </div>
          ) : (
            <div
              className={`overflow-x-auto flex flex-col h-full transition-opacity duration-150 ${loading ? "opacity-0 pointer-events-none" : "opacity-100"}`}
              aria-hidden={loading}
            >
              <div className="px-3 flex items-center justify-end gap-2 py-1.5 flex-shrink-0">
                <VariableSelectionDropdown
                  availableVariables={availableVariables}
                  selectedVariables={selectedVariables}
                  onSelectedVariablesChange={(vars) =>
                    setVisibleColumnKeys(["name", ...vars])
                  }
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-xs h-7"
                  onClick={handleExportCSV}
                  title="Download as CSV"
                >
                  <Download className="h-4 w-4" />
                </Button>
              </div>

              <div className="border-x border-t relative">
                <ScrollArea>
                  <div style={{ width: "max-content", minWidth: "100%" }}>
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10 shadow-sm">
                        <TableRow>
                          {columns.map((col) => (
                            <TableHead key={col.key} style={{ width: col.width }}>
                              <Button
                                variant="ghost"
                                onClick={() => handleSort(col.key)}
                                className="p-0 h-auto font-medium text-xs flex items-center gap-1"
                              >
                                <span className="break-words">{col.label}</span>
                                <ArrowUpDown className="h-3 w-3 flex-shrink-0" />
                              </Button>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {paginatedData.length > 0 ? (
                          paginatedData.map((row, idx) => (
                            <TableRow
                              key={row.rowId || idx}
                              onClick={() => {
                                const original = dedupedImpacts.get(row.rowId);
                                if (original && onRowClick) onRowClick(original);
                              }}
                              className={onRowClick ? "cursor-pointer hover:bg-muted/50" : ""}
                            >
                              {columns.map((col) => (
                                <TableCell
                                  key={col.key}
                                  className="p-2 text-xs"
                                  style={{ width: col.width }}
                                >
                                  <div className="break-words whitespace-normal overflow-hidden">
                                    {fmt(row[col.key as keyof typeof row])}
                                  </div>
                                </TableCell>
                              ))}
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell
                              colSpan={columns.length}
                              className="h-20 text-center text-xs"
                            >
                              No results.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <ScrollBar orientation="horizontal" />
                </ScrollArea>
              </div>

              <div className="flex items-center justify-between px-3 py-1 flex-shrink-0 bg-background border-x border-b">
                <div className="space-x-2 flex items-center">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                    disabled={pageIndex === 0}
                  >
                    Previous
                  </Button>
                  <span className="text-xs">
                    Page {pageIndex + 1} of {pageCount || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => setPageIndex((p) => Math.min(pageCount - 1, p + 1))}
                    disabled={pageIndex >= pageCount - 1}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </AbstractInset>
  );
};

export { WIPImpactsTable };
export type { WIPImpactsTableProps };
